import {PIXI} from '@colonist/pixi'
import {createDivIn} from '../ui_utils/DomUtils'
import {UIPlayerOptionsPopupOptionView} from './UIPlayerOptionsPopupOptionView'
import {UIMutePlayer} from './UIMutePlayer'
import {UIPopupReportPlayer} from '../ui_popup/UIPopupReportPlayer'
import {UIUtilsFriends} from '../ui_friends/UIUtilsFriends'
import {ViewPopup} from '../ui_popup/ViewPopup'
import {UserReportReasonEnum} from '@colonist/game-base-types'
import {UIUtilsEjsVariables} from '../UIUtilsEjsVariables'
import {UIGameManager} from '../ui_game/UIGameManager'
import {PlayerValidator} from '@colonist/game-mechanic-validators'
import {ClientUserState} from '../ClientUserState'
import {doActionIfCanGiftMembership} from '../store/StoreUtils'
import {AjaxDataTypes} from '../../../../shared/AjaxDataTypes'
import {AnalyticEventCategory, AnalyticEventType, GiftMembershipMethod} from '../../../../shared/entity_datas/EntityDatas'
import {CA} from '../analytics/Analytics'
import {uiManagerEvents, socket} from '../EntryShared'
import {UIAPIEndpoints} from '../network/UIAPIEndpoints'
import {UIAjaxWrapper} from '../network/UIAjaxWrapper'
import {navigateToPage} from '../ui_utils/ClientUtils.ts'
import {UIUrlParameterNames} from '../ui_html/UIUrlController.ts'

export enum UIPlayerOptionButtonType {
    Report, Mute, Profile, AddFriend, BlockTrades, Gift
}

export class UIPlayerOptionsPopup {
    private readonly uiGameManager: UIGameManager

    readonly container: HTMLDivElement
    readonly contentContainer: HTMLDivElement

    private topButton?: UIPlayerOptionsPopupOptionView
    private bottomButton?: UIPlayerOptionsPopupOptionView
    private readonly player: PlayerValidator

    private readonly popupPositionRelativeToScreen: PIXI.Point
    private readonly optionButtons: UIPlayerOptionsPopupOptionView[]

    get isPlayerMyUser(): boolean {
        return this.player.userState.userId === ClientUserState.userState?.id
    }

    get isPlayerMyPerspective(): boolean {
        return this.player.color === this.uiGameManager.gameController.myColor
    }

    constructor(uiGameManager: UIGameManager, popupPositionRelativeToScreen: PIXI.Point, player: PlayerValidator) {
        this.uiGameManager = uiGameManager
        this.container = createDivIn(document.body, 'player-options-dropdown-container')
        window.onpointerdown = (event) => {
            if(event.target == this.container) {
                this.removeDropdown()
            }
        }

        this.player = player
        this.popupPositionRelativeToScreen = popupPositionRelativeToScreen
        this.optionButtons = []
        this.contentContainer = createDivIn(this.container, 'player-options-dropdown-content')

        if(UIUtilsEjsVariables.isUserOnMobileApp) return
        window.addEventListener('resize', () => this.updatePopupAttributes())
    }

    buildInGamePlayerOptionsPopup() {
        this.addGiftButton(false)
        this.addAddFriendButton()
        this.addProfileButton()
        this.addBlockTradesButton()
        this.addMuteButton()
        this.addReportButton()

        this.styleButtons()
    }

    buildInGameBotOptionsPopup() {
        this.addBlockTradesButton()
        this.styleButtons()
    }

    buildEndGamePlayerOptionsPopup() {
        this.addGiftButton(true)
        this.addAddFriendButton()
        this.addProfileButton()
        this.addReportButton()
        this.styleButtons()
    }

    private styleButtons() {
        this.assignTopAndBottomButtons()
        this.setHeights()
        this.updatePopupAttributes()
    }

    private addGiftButton(openInternally: boolean) {
        if(UIUtilsEjsVariables.isUserOnDiscordMobile) return
        if(UIUtilsEjsVariables.isUserOnMobileApp && !openInternally) return
        if(this.isPlayerMyUser) return
        if(this.player.isLocalDiscordPlayer) return

        const button = new UIPlayerOptionsPopupOptionView(this.contentContainer, UIPlayerOptionButtonType.Gift, this.player.username, this.player.color)
        const giftButtonClickEventData: AjaxDataTypes.AnalyticData = {
            type: AnalyticEventType.ABTestClickAnalytics,
            category: AnalyticEventCategory.GiftMembershipOverlay,
            floatValue: GiftMembershipMethod.InGame,
        }
        button.setAction(() => {
            void CA.sendAnalyticEvent(giftButtonClickEventData)

            if(UIUtilsEjsVariables.isUserOnDiscord && openInternally) {
                doActionIfCanGiftMembership(() => {
                    uiManagerEvents.handleUrlRedirectViaParameters(UIUrlParameterNames.DiscordGifting, this.player.username)
                }, this.player.username)
            } else {
                const url = (UIUtilsEjsVariables.isUserOnDiscord ? 'https://colonist.io' : '') + `/store?username=${this.player.username}#gift`
                doActionIfCanGiftMembership(() => navigateToPage(url, true), this.player.username)
            }

            this.removeDropdown()
        })
        this.optionButtons.push(button)
    }

    private addAddFriendButton() {
        if(this.player.isLocalDiscordPlayer) return
        if(this.isPlayerMyUser) return

        const button = new UIPlayerOptionsPopupOptionView(this.contentContainer, UIPlayerOptionButtonType.AddFriend, this.player.username, this.player.color)
        button.setAction(() => {
            void this.addFriendButtonAction()
        })
        this.optionButtons.push(button)
    }

    private addProfileButton() {
        if(this.player.isLocalDiscordPlayer) return
        if(UIUtilsEjsVariables.isUserOnMobileApp && this.isPlayerMyUser) return

        const button = new UIPlayerOptionsPopupOptionView(this.contentContainer, UIPlayerOptionButtonType.Profile, this.player.username, this.player.color)
        button.setAction(() => this.viewProfileButtonAction())
        this.optionButtons.push(button)
    }

    private addMuteButton() {
        if(this.uiGameManager.gameController.isReplay) return
        if(this.player.isLocalDiscordPlayer) return
        if(this.isPlayerMyPerspective) return
        if(UIUtilsEjsVariables.isUserOnDiscordMobile) return

        const button = new UIPlayerOptionsPopupOptionView(this.contentContainer, UIPlayerOptionButtonType.Mute, this.player.username, this.player.color)
        button.setAction(() => {
            this.muteButtonAction()
        })
        this.optionButtons.push(button)
    }

    private addBlockTradesButton() {
        if(this.uiGameManager.gameController.isReplay) return
        if(this.isPlayerMyPerspective) return

        const button = new UIPlayerOptionsPopupOptionView(this.contentContainer, UIPlayerOptionButtonType.BlockTrades, this.player.username, this.player.color)
        button.setAction(() => {
            this.uiGameManager.socketGameSend.toggleEmbargoAgainstPlayer(this.player.color)
            this.removeDropdown()
        })
        this.optionButtons.push(button)
    }

    private addReportButton() {
        if(this.player.isLocalDiscordPlayer) return
        if(this.isPlayerMyUser) return

        const button = new UIPlayerOptionsPopupOptionView(this.contentContainer, UIPlayerOptionButtonType.Report, this.player.username, this.player.color)
        button.setAction(() => {
            this.reportButtonAction()
        })
        this.optionButtons.push(button)
    }

    private removeDropdown() {
        this.container.style.display = 'none'
        if(this.container.parentElement != undefined) this.container.parentElement.removeChild(this.container)
    }

    private reportButtonAction() {
        UIPopupReportPlayer.createReportPopup(this.player.username, (playerName: string, reportReason: UserReportReasonEnum) => {
            const databaseGameId = socket.gameListener.databaseGameId
            const userReportData: AjaxDataTypes.UserReportData = {reason: reportReason, databaseGameId: databaseGameId}
            void UIAjaxWrapper.postRequestHandler(UIAPIEndpoints.apiReportPlayer(playerName), undefined, userReportData, 'application/json')
            this.uiGameManager.socketGameSend.reportPlayer(reportReason, this.player.color)
        })
        this.removeDropdown()
    }

    private muteButtonAction() {
        UIMutePlayer.toggleMuteForPlayer(this.player.username)
        this.removeDropdown()
    }

    private async addFriendButtonAction() {
        this.createAddFriendPopup()
        this.removeDropdown()
    }

    private viewProfileButtonAction() {
        uiManagerEvents.openProfilePage(this.player.username, true)
        this.removeDropdown()
    }

    private createAddFriendPopup() {
        new ViewPopup({key: 'strings:popups.addFriend.title'}, {key: 'strings:popups.addFriend.body', options: {
            username: this.player.username,
        }}, () => void UIUtilsFriends.addFriendAction(this.player.username), () => undefined, true).show()
    }

    private updatePopupAttributes() {
        this.updateAppearance()
        this.updatePosition()
    }

    private updatePosition() {
        const canvas = this.uiGameManager.canvasController
        let leftMostPixelPosition = (this.popupPositionRelativeToScreen.x * canvas.app.view.offsetWidth + canvas.responsiveController.getCanvasX())
        let topMostPixelPosition = (this.popupPositionRelativeToScreen.y * canvas.app.view.offsetHeight + canvas.responsiveController.getCanvasY())

        if(leftMostPixelPosition + this.contentContainer.offsetWidth > window.innerWidth) {
            leftMostPixelPosition = Math.max(0, window.innerWidth - this.contentContainer.offsetWidth)
        }
        if(topMostPixelPosition + this.contentContainer.offsetHeight > window.innerHeight) {
            topMostPixelPosition = Math.max(0, window.innerHeight - this.contentContainer.offsetHeight)
        }

        this.contentContainer.style.left = (leftMostPixelPosition / window.innerWidth) * 100 + '%'
        this.contentContainer.style.top = (topMostPixelPosition / window.innerHeight) * 100 + '%'
    }

    private updateAppearance() {
        const defaultButtonCount = 4
        const canvas = this.uiGameManager.canvasController
        const itemSizeRelativeToScreen = canvas.itemSize / canvas.appH

        const popupH = itemSizeRelativeToScreen * 2 * canvas.app.view.offsetHeight * (this.optionButtons.length / defaultButtonCount)

        this.contentContainer.style.width = 'fit-content'
        this.contentContainer.style.height = popupH + 'px'

        const borderRadius = (itemSizeRelativeToScreen * 0.1) * canvas.app.view.offsetHeight
        this.contentContainer.style.borderRadius = borderRadius + 'px'

        if(this.optionButtons.length == 1) {
            this.optionButtons[0].buttonContainer.style['border-radius'] = borderRadius + 'px'
            return
        }

        if(this.topButton != undefined) this.topButton.buttonContainer.style['border-radius'] = `${borderRadius}px ${borderRadius}px 0 0`
        if(this.bottomButton != undefined) this.bottomButton.buttonContainer.style['border-radius'] = `0 0 ${borderRadius}px ${borderRadius}px`
    }

    private setHeights() {
        this.setContainerHeight()
        this.setButtonHeights()
    }

    private assignTopAndBottomButtons() {
        this.topButton = this.optionButtons[0]
        this.bottomButton = this.optionButtons[this.optionButtons.length - 1]
        if(this.optionButtons.length == 1) {
            this.topButton?.setButtonAsSingleButton()
            this.bottomButton?.setButtonAsSingleButton()
            return
        }
        this.topButton?.setButtonAsTopButton()
        this.bottomButton?.setButtonAsBottomButton()
    }

    private setContainerHeight() {
        const canvas = this.uiGameManager.canvasController
        const itemSizeRelativeToScreen = canvas.itemSize / canvas.appH
        const buttonCount = this.optionButtons.length
        const defaultButtonCount = 4
        const popupH = itemSizeRelativeToScreen * 2 * canvas.app.view.offsetHeight * (buttonCount / defaultButtonCount)
        this.contentContainer.style.height = popupH + 'px'
    }

    private setButtonHeights() {
        const buttonCount = this.optionButtons.length
        const buttonHeight = (100 / buttonCount) + '%'
        for(const child of this.contentContainer.children) {
            if(!(child instanceof HTMLDivElement)) continue
            child.style.height = buttonHeight
        }
    }
}
